import React, { Component } from 'react';
import { Select, Table } from 'antd';
import { ChartColumn, Store, CircleCheck, CircleX, Gauge, Target, TriangleAlert, Tags } from 'lucide-react';
import Inherit from '../../core/inherit';
import withNavigate from '../../core/withNavigate';
import SOSAnalysisController from './sos-analysis.controller';
import { Layout, CardSkeleton, AnalyticsSkeleton, PageHeader, StatCard, SectionCard, FilterBar, FilterField, StatusTag, EmptyState } from '../common';
import { StoreCell, ComplianceCell, CategoryCell, SOSCalculationDetail } from './elements';

const { Option } = Select;

const HEADER = {
    icon: <ChartColumn />,
    iconTone: 'violet',
    title: 'Análisis Share of Shelf',
    description: 'Monitorea el cumplimiento de objetivos SOS por etiqueta'
};

class ShelfSOSAnalysis extends Inherit(SOSAnalysisController, Component) {
    state = {
        loading: true,
        tags: [],
        selectedTag: null,
        selectedStore: null,
        storesData: [],
        analysisLoading: false
    };

    render() {
        const { tags, selectedTag, selectedStore, storesData, loading, analysisLoading } = this.state;

        if (loading) {
            return (
                <Layout current="sos-analysis">
                    <PageHeader {...HEADER} />
                    <div className="space-y-4">
                        <AnalyticsSkeleton count={4} />
                        <CardSkeleton rows={8} />
                    </div>
                </Layout>
            );
        }

        const selectedTagData = this.getSelectedTagData();
        const displayStoresData = this.getDisplayStoresData();
        const singleStore = selectedStore && selectedStore !== 'all';

        const columns = [
            {
                title: 'Tienda',
                dataIndex: 'storeName',
                key: 'storeName',
                fixed: 'left',
                width: 220,
                render: (text, record) => <StoreCell name={text} lastCheckDate={record.lastCheckDate} totalFaces={record.totalFaces} />
            },
            {
                title: 'Cumplimiento General',
                key: 'overall',
                width: 170,
                render: (_, record) => <ComplianceCell compliance={this.calculateOverallCompliance(record)} />
            },
            ...(selectedTagData?.sosTargets || []).map((target) => ({
                title: target.category,
                key: target.category,
                width: 180,
                render: (_, record) => (
                    <CategoryCell categoryData={this.findCategoryData(record, target.category)} targetPercentage={target.targetPercentage} />
                )
            }))
        ];

        const summaryStats = this.calculateSummaryStats(displayStoresData);
        const avgCompliance = this.getAverageCompliance(summaryStats);
        const avgGood = avgCompliance >= 70;

        return (
            <Layout current="sos-analysis">
                {this.props.contextHolder}

                <PageHeader {...HEADER} />

                <div className="space-y-4">
                    {/* Filters */}
                    <FilterBar className="!mb-0">
                        <FilterField label="Seleccionar Etiqueta:" className="w-full sm:w-80">
                            <Select
                                className="w-full"
                                placeholder="Selecciona una etiqueta con objetivos SOS"
                                value={selectedTag}
                                onChange={this.handleTagChange.bind(this)}
                                loading={loading}
                                notFoundContent={<span className="text-xs text-surface-500">No hay etiquetas con objetivos SOS</span>}>
                                {tags.map((tag) => (
                                    <Option key={tag._id} value={tag._id}>
                                        <div className="flex items-center gap-2">
                                            <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: tag.color }} />
                                            <span className="truncate">{tag.name}</span>
                                            <span className="text-surface-400">({tag.sosTargets?.length || 0} categorías)</span>
                                        </div>
                                    </Option>
                                ))}
                            </Select>
                        </FilterField>

                        {selectedTag && selectedTagData && storesData.length > 0 && !analysisLoading && (
                            <FilterField label="Filtrar por tienda:" className="w-full sm:w-80">
                                <Select
                                    className="w-full"
                                    placeholder="Todas las tiendas"
                                    value={selectedStore}
                                    onChange={(value) => this.setSelectedStore(value)}
                                    allowClear
                                    onClear={() => this.setSelectedStore(null)}
                                    suffixIcon={<Store className="text-surface-400" />}>
                                    <Option value="all">Todas las tiendas ({storesData.length})</Option>
                                    {storesData.map((store) => (
                                        <Option key={store.storeId} value={store.storeId}>
                                            {store.storeName} - {store.totalFaces} caras
                                        </Option>
                                    ))}
                                </Select>
                            </FilterField>
                        )}
                    </FilterBar>

                    {/* SOS Targets of the selected tag */}
                    {selectedTag && selectedTagData && (
                        <div className="shelf-card flex flex-col gap-3 px-5 py-4 md:flex-row md:items-center">
                            <span className="flex shrink-0 items-center gap-2 text-sm font-medium text-surface-700">
                                <Target className="text-violet-fg" />
                                Objetivos de Share of Shelf
                            </span>
                            <div className="flex flex-wrap gap-1.5">
                                {selectedTagData.sosTargets.map((target) => (
                                    <StatusTag key={target.category} tone="violet" className="tabular-nums">
                                        {target.category}: <strong className="font-semibold">{target.targetPercentage}%</strong>
                                    </StatusTag>
                                ))}
                            </div>
                        </div>
                    )}

                    {!selectedTag && (
                        <div className="shelf-card">
                            <EmptyState
                                icon={<Tags />}
                                title="Selecciona una etiqueta"
                                description={
                                    tags.length
                                        ? 'Elige una etiqueta con objetivos SOS para ver el cumplimiento de sus tiendas.'
                                        : 'Aún no hay etiquetas con objetivos SOS. Configúralos desde Sistema de Etiquetas.'
                                }
                            />
                        </div>
                    )}

                    {selectedTag && storesData.length > 0 && (
                        <>
                            {/* Summary Stats */}
                            <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 xl:grid-cols-4">
                                <StatCard label="Tiendas Analizadas" value={summaryStats.totalStores} icon={<Store />} tone="violet" hint="Con auditorías recientes" />
                                <StatCard
                                    label="Cumplimiento Promedio"
                                    value={`${avgCompliance.toFixed(1)}%`}
                                    icon={<Gauge />}
                                    tone={avgGood ? 'success' : 'danger'}
                                    valueTone={avgGood ? 'success' : 'danger'}
                                    hint="Share of Shelf general"
                                />
                                <StatCard
                                    label="Tiendas en Meta"
                                    value={`${summaryStats.storesInTarget}/${summaryStats.totalStores}`}
                                    icon={<CircleCheck />}
                                    tone="success"
                                    valueTone="success"
                                    hint="Cumpliendo objetivos"
                                />
                                <StatCard
                                    label="Tiendas Bajo Meta"
                                    value={`${summaryStats.storesBelowTarget}/${summaryStats.totalStores}`}
                                    icon={<CircleX />}
                                    tone="danger"
                                    valueTone="danger"
                                    hint="Requieren atención"
                                />
                            </div>

                            {/* Calculation Detail (when single store selected) */}
                            {singleStore && displayStoresData.length > 0 && <SOSCalculationDetail store={displayStoresData[0]} />}

                            {/* Analysis Table */}
                            <SectionCard flush icon={<ChartColumn />} title={singleStore ? 'Análisis Detallado' : 'Análisis por Tienda'}>
                                <div className="overflow-x-auto">
                                    <Table
                                        columns={columns}
                                        dataSource={displayStoresData}
                                        loading={analysisLoading}
                                        rowKey="storeId"
                                        pagination={{
                                            pageSize: 20,
                                            showTotal: (total) => `Total ${total} tiendas`
                                        }}
                                        scroll={{ x: 1200 }}
                                        size="middle"
                                    />
                                </div>
                            </SectionCard>
                        </>
                    )}

                    {selectedTag && analysisLoading && storesData.length === 0 && <CardSkeleton rows={6} />}

                    {selectedTag && storesData.length === 0 && !analysisLoading && (
                        <div className="shelf-card">
                            <EmptyState
                                icon={<TriangleAlert />}
                                title="Sin Datos"
                                description="No se encontraron tiendas con esta etiqueta que tengan auditorías recientes."
                            />
                        </div>
                    )}
                </div>
            </Layout>
        );
    }
}

export default withNavigate(ShelfSOSAnalysis);
